<template>
  <div class="">
    <h3>Live Market Data</h3>
    <div class="flex flex-col w-full items-center justify-center bg-gray-900 text-gray-300 text-left">

      <div class="flex flex-col w-full mb-5 px-4">
        <p @click="isArrangeHeadersCollapsed = !isArrangeHeadersCollapsed" class="text-sm mb-2 cursor-pointer">
          Arrange Headers
          <span class="inline-block transition-transform duration-300"
            :class="{'rotate-180': isArrangeHeadersCollapsed}">&#9660;</span>
        </p>
        <div class="pl-2 w-full overflow-y-auto bg-gray-800 rounded transition-all duration-500"
          :class="{'max-h-[550px]': !isArrangeHeadersCollapsed, 'max-h-0 overflow-hidden': isArrangeHeadersCollapsed}">
          <button @click="resetHeaders" class="text-xs text-blue-400 hover:text-blue-300 px-2">Reset</button>
          <vue-draggable-next v-model="headersList" tag="ul" :ghost-class="'drag-ghost'" :chosen-class="'drag-chosen'"
            @start="({ item }) => draggedId = Number(item.id)" @end="onDragEnd">
            <transition-group name="flip-list">
              <li :id="element.id" v-for="element in headersList" :key="element.id"
                class="flex items-center gap-2 cursor-move p-2 bg-gray-600 m-2 rounded-md transition-transform duration-500"
                :class="{
                  'bg-green-300 text-black': droppedId === element.id,
                }">
                <span class="px-2" title="Drag to reorder">⠿</span>
                <span>{{ element.text }}</span>
              </li>
            </transition-group>
          </vue-draggable-next>
        </div>
      </div>

      <div class="flex flex-col w-full mb-5 max-h-80 px-4">
        <p class="text-sm mb-2">Live data console:</p>
        <div class="h-48 pl-2 w-full overflow-y-auto bg-black rounded font-mono" ref="consoleOutput">
          <ul>
            <li v-for="(update, index) in updates" :key="index" class="text-xs my-1 text-green-600">{{ update }}</li>
          </ul>
        </div>
        <p class="text-sm mt-2">Data feed status: {{ currentStatus == '' ? '...' : currentStatus }}</p>
        <p class="text-sm">Last updated: {{ lastRefreshTime ? lastRefreshTime.format('HH:mm:ss') : 'Never' }}</p>
        <button 
          @click="loadAssetPrices" 
          :disabled="isRefreshing"
          :class="['mt-3 btn btn-primary', { 'btn-disabled': isRefreshing }]"
        >
          {{ isRefreshing ? 'Refreshing...' : 'Refresh Asset Prices' }}
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>

import { ref, onMounted, onBeforeUnmount, watch, nextTick } from "vue";
import axios from "axios";
import { buildConnectionOptions, connectionKey } from "../liveConnection.mjs";
import dayjs from "dayjs";
import { VueDraggableNext } from "vue-draggable-next";

const emit = defineEmits(["MissingCredentials"]);

const props = defineProps({
  company: {
    type: Object,
    default: () => ({})
  },
  environment: {
    type: String,
    default: ""
  },
  environmentURL: {
    type: String,
    default: ""
  },
  apiKey: {
    type: String,
    default: ""
  }
});

const company = ref(props.company);
const environment = ref(props.environment);
const environmentURL = ref(props.environmentURL);
const apiKey = ref(props.apiKey);
const currentStatus = ref("");

const listening = ref(false);
const pusher = ref(null);
const updates = ref([]);
const rowUpdateQueue = ref([]);
let processingRowUpdate = false;

const consoleOutput = ref(null);
const isArrangeHeadersCollapsed = ref(true);
const isRefreshing = ref(false);
const lastRefreshTime = ref(null);
let connectionCheckInterval = null;


const defaultHeadersList = [
  { id: 1, text: "Asset Code", key: "asset_code" },
  { id: 2, text: "Region", key: "region" },
  { id: 3, text: "Type", key: "type" },
  { id: 4, text: "Hours", key: "hours" },
  { id: 5, text: "State", key: "state" },
  { id: 6, text: "Global Bid Price", key: "global_bid_price" },
  { id: 7, text: "Global Bid Quantity", key: "global_bid_quantity" },
  { id: 8, text: "Global Bid Ours", key: "global_bid_ours" },
  { id: 9, text: "Global Offer Price", key: "global_ask_price" },
  { id: 10, text: "Global Offer Quantity", key: "global_ask_quantity" },
  { id: 11, text: "Global Offer Ours", key: "global_ask_ours" },
  { id: 12, text: "Connection Bid Price", key: "connection_bid_price" },
  { id: 13, text: "Connection Bid Quantity", key: "connection_bid_quantity" },
  { id: 14, text: "Connection Bid Ours", key: "connection_bid_ours" },
  { id: 15, text: "Connection Offer Price", key: "connection_ask_price" },
  { id: 16, text: "Connection Offer Quantity", key: "connection_ask_quantity" },
  { id: 17, text: "Connection Offer Ours", key: "connection_ask_ours" },
  { id: 18, text: "Last Trade Price", key: "last_trade_price" },
  { id: 19, text: "Close Price", key: "open_price" },
  { id: 21, text: "Last Updated At", key: "time_stamp" }
];

const headersList = ref([...defaultHeadersList]);

const savedHeadersOrder = localStorage.getItem("headersListOrder");
if (savedHeadersOrder) {
  try {
    const orderedKeys = JSON.parse(savedHeadersOrder);
    const headersMap = new Map(headersList.value.map(item => [item.key, item]));
    const reorderedList = orderedKeys.map(key => headersMap.get(key)).filter(Boolean);
    const currentKeys = new Set(reorderedList.map(item => item.key));
    const newHeaders = headersList.value.filter(item => !currentKeys.has(item.key));
    headersList.value = [...reorderedList, ...newHeaders];
  } catch (e) {
    console.error("Failed to parse headersListOrder from localStorage", e);
  }
}

const draggedId = ref(null);
const droppedId = ref(null);

const onDragEnd = async () => {
  droppedId.value = draggedId.value;
  draggedId.value = null;
  setTimeout(() => {
    droppedId.value = null;
  }, 600);
  localStorage.setItem("headersListOrder", JSON.stringify(headersList.value.map(h => h.key)));
  addUpdate('Headers order updated');
  await reorderExcelColumns();
};

async function reorderExcelColumns() {
  processingRowUpdate = true; // block all other updates while reordering
  try {
    await Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getItem("TOD_LIVE");
      const table = sheet.tables.getItem("AssetPricesTable");

      const headerRange = table.getHeaderRowRange();
      const dataRange = table.getDataBodyRange();
      headerRange.load("values");
      dataRange.load("values");
      await context.sync();

      const oldHeaders = headerRange.values[0];
      const tableData = dataRange.values;
      const newHeadersText = headersList.value.map(h => h.text);

      // If there's no data, just update the headers and exit.
      if (tableData.length === 0) {
        headerRange.values = [newHeadersText];
        table.getRange().format.autofitColumns();
        await context.sync();
        addUpdate("Headers reordered successfully.");
        return;
      }

      const oldHeaderIndexMap = new Map(oldHeaders.map((header, index) => [header, index]));

      const reorderedData = tableData.map(row =>
        newHeadersText.map(header => row[oldHeaderIndexMap.get(header)])
      );

      headerRange.values = [newHeadersText];
      dataRange.values = reorderedData;
      table.getRange().format.autofitColumns();

      await context.sync();
      addUpdate("Headers reordered successfully.");
    });
  } catch (error) {
    console.error("Error reordering Excel columns:", error);
    addUpdate("Failed to reorder headers.");
  }
  finally {
    processingRowUpdate = false; // allow other updates after reordering
  }
}

async function resetHeaders() {
  addUpdate("Resetting headers to default order...");
  headersList.value = [...defaultHeadersList];
  localStorage.removeItem("headersListOrder");
  await reorderExcelColumns();
}

/**
 * Queues a row update for processing.
 * @param rowData the data from the websocket message
 */
function queueRowUpdate(rowData) {
  if (rowUpdateQueue.value.findIndex(item => item.asset_code === rowData.asset_code &&
    item.time_stamp === rowData.time_stamp
  ) !== -1) {
    console.warn(`Row with asset code ${rowData.asset_code} and timestamp ${rowData.time_stamp} is already in the queue.`);
    return;
  }

  rowUpdateQueue.value.push(rowData);
  rowUpdateQueue.value = [...rowUpdateQueue.value];
}

async function addUpdate(message) {
  updates.value.push(message);
  if (updates.value.length > 50) {
    updates.value.splice(0, updates.value.length - 50); // Keep only the last 50 updates
  }
  // Scroll to the bottom of the console output using a ref
  if (consoleOutput.value) {
    const element = consoleOutput.value;
    await nextTick();
    element.scrollTop = element.scrollHeight;
  }

}

const channels = ref({
  "system-health": {
    name: () => "system-health",
    instance: null,
    events: { "App\\Events\\SystemHeartbeat": (data) => addUpdate(`Connection: ok`) }
  },
  "private-auth-market": {
    name: () => "private-auth-market",
    instance: null,
    events: {
      "App\\Events\\MarketOpened": (e) => addUpdate("Market opened"),
      "App\\Events\\MarketClosed": (e) => addUpdate("Market closed")
    }
  },
  "private-company": {
    name: () => `private-${company.value.channel_key}`,
    instance: null,
    events: {
      "App\\Events\\AssetPriceChangeEventCompany": (e) => queueRowUpdate(e.data)
    }
  }
});

async function loadAssetPrices() {
  if (!apiKey.value) {
    console.error("API key is not set. Cannot fetch asset prices.");
    emit("MissingCredentials");
    return;
  }

  isRefreshing.value = true;
  
  const regions = ["Q", "N", "V", "S", "T"];
  const assetPrices = await Promise.all(
    regions.map(getAssetPricesForRegion)
  );
  await addUpdate("Asset prices fetched successfully. Writing to Excel...");

  try {

    await Excel.run(async (context) => {
      const sheet = await getOrCreateTODLiveSheet(context);

      const currentHeaders = headersList.value.map(h => h.text);
      sheet.getRangeByIndexes(0, 0, 1, currentHeaders.length).values = [currentHeaders];
      sheet.getRangeByIndexes(0, 0, 1, currentHeaders.length).format.autofitColumns();

      let row = 1;
      const orderedKeys = headersList.value.map(h => h.key);
      for (const prices of assetPrices) {
        for (const asset of prices) {
          const values = [orderedKeys.map(key => {
            if (key.endsWith("_ours")) {
              return asset[key] ? true : false;
            }
            return asset[key] || "";
          })];

          sheet.getRangeByIndexes(row, 0, 1, values[0].length).values = values;
          row++;
        }
      }

      const totalRows = row;
      const totalCols = currentHeaders.length;


      let table;


      try {
        table = sheet.tables.getItem("AssetPricesTable");
        addUpdate("Table found, updating existing table.");
        await context.sync();
      } catch (e) {
        addUpdate("Table not found, creating a new one.");

        const dataRange = sheet.getRangeByIndexes(0, 0, totalRows, totalCols);
        dataRange.load("address");
        await context.sync();

        table = sheet.tables.add(dataRange.address, true);
        table.name = "AssetPricesTable";
        await context.sync();
      }

      table.columns.load("items");
      await context.sync();


      table.columns.items.forEach(col => {
        col.getRange().format.autofitColumns();
      });

      if (environment.value === "live") {
        table.style = "TableStyleDark8"; // black
      } else if (environment.value === "staging") {
        table.style = "TableStyleMedium5"; // blue
      } else if (environment.value === "dev") {
        table.style = "TableStyleMedium3"; // orange
      } else if (environment.value === "local") {
        table.style = "TableStyleMedium6"; // pink
      }

      addUpdate("Asset prices written to the sheet successfully.");
    });

  } catch (error) {
    console.error("Error writing asset prices to Excel:", error);
    addUpdate("Failed to write asset prices to the sheet.");
  } finally {
    isRefreshing.value = false;
    lastRefreshTime.value = dayjs();
  }
}


function startListening() {
  // Check if there's already a healthy connection
  if (pusher.value && pusher.value.connection) {
    const connectionState = pusher.value.connection.state;
    if (connectionState === "connected") {
      return;
    }
    // Only clean up if connection is unhealthy
    stopListening();
  }

  const key = connectionKey(company.value);
  const options = buildConnectionOptions(company.value, {
    environment: environment.value,
    environmentURL: environmentURL.value,
    apiKey: apiKey.value,
    localProxy: __LOCAL_REVERB_PROXY__,
    location: window.location,
  });
  const host = options.wsHost;
  const cluster = options.cluster;

  /*
   * A cluster only means something to Pusher's cloud. Insisting on one here
   * used to stop every Reverb environment before it connected, because Reverb
   * has no cluster and identifies its endpoint with a host instead.
   */
  if (!key || (!host && !cluster)) {
    console.log(company.value);
    console.error("Live data endpoint is not configured. Cannot start listening.");
    addUpdate("Live data endpoint is not configured. Cannot start listening.");
    return;
  }

  const endpoint = options.wsHost
    ? `${options.forceTLS ? "wss" : "ws"}://${options.wsHost}:${options.forceTLS ? options.wssPort : options.wsPort}${options.wsPath || ""}`
    : `cluster ${cluster}`;
  addUpdate(`Connecting to live data feed (${endpoint})...`);
  addUpdate(`Add-in page origin: ${window.location.origin}`);

  pusher.value = new Pusher(key, options);
  currentStatus.value = pusher.value.connection.state;
  pusher.value.connection.bind("state_change", (states) => {
    addUpdate(`Connection: ${states.previous} → ${states.current}`);
    currentStatus.value = states.current;
  });
  pusher.value.connection.bind("error", (error) => {
    addUpdate(`Connection error: ${error.error?.data?.message || error.message || error.type || "WebSocket connection failed"}`);
  });

  listening.value = true;

  for (const config of Object.values(channels.value)) {
    const channel = pusher.value.subscribe(config.name());
    config.instance = channel;
    channel.bind("pusher:subscription_succeeded", () => {
      addUpdate(`Subscribed to ${config.name()}`);
    });
    channel.bind("pusher:subscription_error", (error) => {
      addUpdate(`Subscription failed for ${config.name()}: ${error.status || error.type || "authorization failed"}`);
    });

    for (const [event, handler] of Object.entries(config.events)) {
      channel.bind(event, handler);
    }
  }
}

function stopListening() {
  if (!pusher.value) return;

  for (const [name, config] of Object.entries(channels.value)) {
    const channel = config.instance;
    if (!channel) continue;

    if (config.events) {
      for (const event of Object.keys(config.events)) {
        channel.unbind(event);
      }
    }

    pusher.value.unsubscribe(config.name());
    config.instance = null;
  }

  pusher.value.disconnect();
  pusher.value = null;
  listening.value = false;
}

async function checkConnectionAndRefresh() {
  // Prevent overlapping refresh operations
  if (isRefreshing.value) {
    return;
  }

  const connectionState = pusher.value?.connection?.state;

  if (connectionState !== "connected") {
    addUpdate("⚠️ Auto-refresh triggered: Live connection not available - reconnecting...");
    await loadAssetPrices();
    startListening();
    return;
  }
}

function startConnectionMonitoring() {
  if (connectionCheckInterval) {
    clearInterval(connectionCheckInterval);
  }
  
  connectionCheckInterval = setInterval(async () => {
    try {
      await checkConnectionAndRefresh();
    } catch (error) {
      console.error("Error during periodic connection check:", error);
    }
  }, 60000); // 60 seconds
}

function stopConnectionMonitoring() {
  if (connectionCheckInterval) {
    clearInterval(connectionCheckInterval);
    connectionCheckInterval = null;
  }
}

async function getAssetPricesForRegion(region) {
  if (!apiKey.value) {
    console.error("API key is not set. Cannot fetch asset prices.");
    return;
  }

  try {
    addUpdate(`Fetching asset prices for region ${region}...`);
    const response = await axios.get(`${environmentURL.value}/assets/prices`, {
      headers: {
        "Authorization": `Bearer ${apiKey.value}`
      },
      params: {
        regions: region
      }
    });
    return response.data.asset_prices;
  } catch (error) {
    console.error(`Error fetching asset prices for region ${region}:`, error);
    addUpdate(`Failed to fetch asset prices for region ${region}.`);
    return [];
  }
}

async function replaceRowByAssetCode(asset) {
  let assetCode = asset.asset_code;
  
  try {
    await Excel.run(async (context) => {
      const sheet = await getOrCreateTODLiveSheet(context);
      const range = sheet.getUsedRange();
      range.load("values");
      await context.sync();

      const values = range.values;
      const headers = values[0];
      const assetCodeColIndex = headers.indexOf("Asset Code");

      if (assetCodeColIndex === -1) {
        console.error("Asset Code column not found.");
        return;
      }

      let rowIndex = -1;
      for (let i = 1; i < values.length; i++) { // Start from 1 to skip header
        if (values[i][assetCodeColIndex] === assetCode) {
          rowIndex = i;
          break;
        }
      }

      if (rowIndex === -1) {
        console.warn(`Asset code "${assetCode}" not found.`);
        return;
      }

      const orderedKeys = headersList.value.map(h => h.key);
      const newRowData = orderedKeys.map(key => {
        if (key.endsWith("_ours")) {
          return asset[key] ? true : false;
        }
        return asset[key] || "";
      });

      const rowRange = sheet.getRangeByIndexes(rowIndex, 0, 1, newRowData.length);
      rowRange.values = [newRowData];
      addUpdate(`${dayjs(asset.time_stamp).format("HH:mm:ss")} ${assetCode} row updated`);
      lastRefreshTime.value = dayjs();
    });
    return true;
  } catch (error) {
    if (
      error &&
      error.message &&
      error.message.includes("Excel is in cell-editing mode")
    ) {
      addUpdate("Waiting for editing to finish...");
      setTimeout(() => {
        queueRowUpdate(asset);
      }, 2000);
    } else {
      console.error("Error updating row in Excel:", error);
      addUpdate("Failed to update row in Excel.");
    }
    return false;
  }
}

watch(rowUpdateQueue, async (queue) => {
  if (processingRowUpdate || queue.length === 0) return;
  processingRowUpdate = true;
  try {
    const rowData = queue.shift();
    await replaceRowByAssetCode(rowData);
    if (rowUpdateQueue.value.length > 0) {
      rowUpdateQueue.value = [...rowUpdateQueue.value];
    } else {
      rowUpdateQueue.value = [];
    }
  } catch (err) {
    console.error("Unhandled error in row update:", err);
  }
  processingRowUpdate = false;

});

async function getOrCreateTODLiveSheet(context) {
  let sheet;
  try {
    sheet = context.workbook.worksheets.getItem("TOD_LIVE");
    // Try to load to check existence
    sheet.load("name");
    await context.sync();
  } catch (e) {
    // If not found, create it
    sheet = context.workbook.worksheets.add("TOD_LIVE");
    await context.sync();
  }
  // prevent any ability to manually edit the sheet - it should basically be read-only
  await context.sync();
  return sheet;
}


onMounted(async () => {
  await loadAssetPrices();
  startListening();
  startConnectionMonitoring();
});

onBeforeUnmount(() => {
  stopListening();
  stopConnectionMonitoring();
});
</script>

<style scoped>
.drag-chosen {
  @apply bg-gray-500 text-black;
}

.drag-ghost {
  @apply bg-yellow-500  opacity-50;
}
</style>
